import React from "react";
import { Activity, Wind, Droplet, Thermometer, HeartPulse } from "lucide-react";

const vitalColor = {
  HR: "text-rose-400",
  BP: "text-amber-300",
  MAP: "text-amber-300",
  RR: "text-indigo-300",
  SpO2: "text-cyan-300",
  Temp: "text-emerald-300",
};

const vitalIcon = {
  HR: HeartPulse,
  BP: Droplet,
  MAP: Droplet,
  RR: Wind,
  SpO2: Activity,
  Temp: Thermometer,
};

const Row = ({ label, value }) => {
  const Icon = vitalIcon[label] || Activity;
  const color = vitalColor[label] || "text-slate-200";
  return (
    <div
      className="flex items-center justify-between border-b border-slate-800 py-3"
      data-testid={`vital-${label.toLowerCase()}`}
    >
      <div className="flex items-center gap-2 text-slate-400">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
        <span className="text-[10px] uppercase tracking-widest font-medium">{label}</span>
      </div>
      <div className={`font-mono text-2xl font-semibold tracking-tight vital-pulse ${color}`}>
        {value || "—"}
      </div>
    </div>
  );
};

export default function VitalsMonitor({ scenario }) {
  if (!scenario) {
    return (
      <div
        className="bg-slate-900 text-slate-300 p-6 rounded-sm border border-slate-800 h-full"
        data-testid="vitals-monitor-empty"
      >
        <div className="flex items-center justify-between mb-6">
          <span className="text-[10px] uppercase tracking-widest text-slate-500">
            Bedside Monitor
          </span>
          <span className="h-2 w-2 rounded-full bg-slate-700"></span>
        </div>
        <p className="text-slate-500 text-sm leading-relaxed">
          Awaiting case. Start a Daily Case or Simulation Drill to populate vitals.
        </p>
      </div>
    );
  }

  const v = scenario.vitals || {};
  return (
    <div
      className="bg-slate-900 text-slate-200 p-6 rounded-sm border border-slate-800 h-full flex flex-col"
      data-testid="vitals-monitor"
    >
      <div className="flex items-center justify-between mb-5">
        <span className="text-[10px] uppercase tracking-widest text-slate-500">
          Bedside Monitor
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
          <span className="text-[10px] uppercase tracking-widest text-emerald-400">Live</span>
        </span>
      </div>

      <div className="mb-5 pb-4 border-b border-slate-800">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Patient</div>
        <div className="font-mono text-sm text-slate-100">
          {scenario.age} {scenario.gender}
        </div>
        <p className="text-xs text-slate-400 mt-2 leading-relaxed">{scenario.context}</p>
      </div>

      <div className="space-y-0">
        <Row label="HR" value={v.HR} />
        <Row label="BP" value={v.BP} />
        {v.MAP && <Row label="MAP" value={v.MAP} />}
        <Row label="RR" value={v.RR} />
        <Row label="SpO2" value={v.SpO2} />
        {v.Temp && <Row label="Temp" value={v.Temp} />}
      </div>

      {scenario.critical_data?.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-800">
          <div className="text-[10px] uppercase tracking-widest text-rose-400 mb-2">
            Critical Data
          </div>
          <ul className="space-y-1.5">
            {scenario.critical_data.map((d, i) => (
              <li key={i} className="font-mono text-xs text-slate-200">
                · {d}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
